import {FC} from 'react';
import {FormikConfig} from 'formik';
import {chargersApi} from 'src/api';
import {useBusinessId, useAppDispatch} from 'src/app/hooks';
import {ChargerForm} from 'src/components/forms/ChargerForm';
import {ChargerType} from 'src/components/forms/formData/charger';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch, AuthUserDTO, ChargePointCreateRequest} from 'src/types';
import {NotifyError} from 'src/utils/notification';
import {ChargerCreateFormWrapperType} from './ChargerCreateFormWrapper.types';
import {IntlShape, useIntl} from 'react-intl';
import {valuesTrimmer} from '../../../utils/helper';
import {UserTypeEnum} from 'src/components/forms/formData/common/initialData';

type ChargerCreateMutationTrigger = ReturnType<typeof chargersApi.useAddChargerMutation>[0];

const handleSubmit =
    (
        trigger: ChargerCreateMutationTrigger,
        dispatch: AppDispatch,
        authInfo: AuthUserDTO,
        // Passed in rather than read off authInfo: the platform administrator has no business of their
        // own, so `authInfo.business` is null for them and this is the business they are working in.
        businessId: number,
        handleClose: () => void,
        intl: IntlShape
    ): FormikConfig<ChargerType>['onSubmit'] =>
        async (value, helpers) => {
            const values = valuesTrimmer(value);

            dispatch(preloaderChangeStatus(true));
            const data: ChargePointCreateRequest = {
                businessId,
                chargePointId: values.chargePointId,
                chargePointName: values.chargePointName,
                modelId: +values.modelId,
            };

            if (values.userType === UserTypeEnum.PRIVATE_DRIVER) {
                data.userId = +values.userId;
            }

            if (values.userType === UserTypeEnum.COMPANY_USER) {
                data.companyId = +values.companyId;
            }

            if (values.locationId) {
                data.locationId = +values.locationId;
            }

            try {
                await trigger(data).unwrap();
                handleClose();
            } catch (error: any) {
                error.status === 409
                    ? NotifyError(intl.formatMessage({id: 'chargePointErrorUnique'}))
                    : NotifyError(`ERROR ${error.data.error}`);
            } finally {
                helpers.setSubmitting(false);
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ChargerCreateFormWrapper: FC<ChargerCreateFormWrapperType> = ({
                                                                               handleClose,
                                                                               authInfo,
                                                                           }) => {
    const businessId = useBusinessId();
    const intl = useIntl();
    const [trigger] = chargersApi.useAddChargerMutation();
    const dispatch = useAppDispatch();

    return (
        <ChargerForm
            handleClose={handleClose}
            handleSubmit={handleSubmit(trigger, dispatch, authInfo, businessId, handleClose, intl)}
            authInfo={authInfo}
        />
    );
};
